<template>
  <v-container fluid class="pa-0 fill-height chat-container">
    <SidebarNav v-if="hasSidebar" :drawer="drawer" @drawer-toggle="toggleDrawer" @drawer-input="setDrawer">
      <template #top="{ drawer }">
        <slot name="sidebar-top" :drawer="drawer">
          <!-- Default sidebar top content can go here -->
        </slot>
      </template>
      <template #conversations>
        <slot name="sidebar-conversations">
          <!-- Default sidebar conversations content can go here -->
        </slot>
      </template>
    </SidebarNav>
    <v-theme-provider :root="!contentDark" :dark="contentDark">
    <div
      class="flex-grow-1 fill-height d-flex flex-column fill-width content-wrapper"
      :class="{ 'content-themed': contentDark }"
    >
      <HeaderLayout @drawer-show="toggleDrawer">
        <template #header>
          <slot name="header">
            <!-- Default header content can go here -->
          </slot>
        </template>
      </HeaderLayout>
      <div class="main-content d-flex flex-column flex-grow-1" :class="contentClass" :style="contentStyle">
        <slot name="content">
          <!-- Main content goes here -->
        </slot>
      </div>
      <div
        v-if="$theme.layout.showFooter"
        class="app-footer text-center caption grey--text text--darken-1 py-2"
        role="button"
        @click="footerDialog = true"
      >
        <slot name="footer">
          © {{ year }} {{ $theme.brand.company }} — {{ $theme.brand.appName }} v{{ version }}
        </slot>
      </div>
    </div>
    </v-theme-provider>
    <v-snackbar v-model="visible" :color="notification?.type === 'error' ? 'red' : 'green'">
      {{ notification?.message }}
    </v-snackbar>
    <v-dialog v-model="footerDialog" max-width="900" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center py-3">
          <span class="subtitle-2 grey--text text--darken-1">À propos</span>
          <v-spacer />
          <v-btn icon small @click="footerDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-0">
          <AboutContent />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import SidebarNav from '@/layout/SidebarNav.vue'
import HeaderLayout from './HeaderLayout.vue'
import AboutContent from '@/components/AboutContent.vue'
import { VThemeProvider } from 'vuetify/lib'
import { version } from '../../package.json'

export default {
  name: 'MainLayout',
  components: { SidebarNav, HeaderLayout, AboutContent, VThemeProvider },
  props: {
    contentClass: {
      type: String,
      default: ''
    },
    hasSidebar: {
      type: Boolean,
      default: true
    },
    contentStyle: {
      type: Object,
      default: () => ({})
    },
    initialDrawerExpanded: {
      type: Boolean,
      default: false  
    }
  },
  data() {
    return {
      drawer: this.initialDrawerExpanded,
      footerDialog: false,
      version,
      year: new Date().getFullYear(),
    }
  },
  watch: {
    $route() {
      this.footerDialog = false
    },
  },
  computed: {
    contentDark() { return !!this.$theme.layout.contentDark },
    userName: {
      get() {
        return this.$store.getters["settings/userName"];
      },
      set(value) {
        this.$store.commit("settings/SET_USER_NAME", value);
      }
    },
    visible: {
      get() { return !!this.notification },
      set(val) { if (!val) this.$store.commit("chat/CLEAR_NOTIFICATION") }
    },
    notification() {
      return this.$store.state['chat'].notification;
    },
    sidebarCols() {
      return this.drawerCollapsed ? 1 : 3;
    },
    contentCols() {
      return this.drawerCollapsed ? 11 : 9;
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleGlobalKeydown)
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleGlobalKeydown)
  },
  methods: {
    toggleDrawer() {
      this.drawer = !this.drawer;
    },
    setDrawer(value) {
      this.drawer = value;
    },
    handleGlobalKeydown(event) {
      if (event.key === 'F1') {
        event.preventDefault()
        this.footerDialog = true
      }
    },
  }
}
</script>

<style scoped>
.container.fill-height > .fluid-row {
  max-width: 100%;
}
.content-wrapper {
  width: 100%;
}
.chat-container {
  /* Vuetify's v-application--wrap/v-main only ever set min-height, never a
     definite height, so height:100% below this point has nothing real to
     resolve against and every nested "fill-height"/overflow region silently
     falls back to auto (grows with content instead of scrolling). Anchor to
     the viewport here so the fill-height chain below actually clips. */
  height: 100vh;
}
/* Themed (coloured) content pane: see theme.layout.contentDark */
.content-themed {
  background: var(--app-content-bg);
  color: var(--app-content-text);
}
.content-themed .app-footer {
  color: rgba(255, 255, 255, 0.7) !important;
  border-top-color: rgba(255, 255, 255, 0.15);
}
.content-themed .app-footer:hover {
  color: #fff !important;
}
.chat-header { border-bottom: 1px solid rgba(0,0,0,0.06); }
.main-content {
  overflow-y: auto;
}
.app-footer {
  flex-shrink: 0;
  font-size: 11px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  cursor: pointer;
}

.app-footer:hover {
  color: rgba(0, 0, 0, 0.7);
}
.transition-all {
  transition: all 0.3s ease;
}
</style>